import { observer } from 'mobx-react-lite';
import { errorImageAttachment } from 'owa-locstrings/lib/strings/errorimageattachment.locstring.json';
import {
    previousAttachment,
    nextAttachment,
    exitFullScreen as exitFullScreen_1,
    imagePreviewAltText,
} from './ImagePreview.locstring.json';
import loc from 'owa-localize';
import { CommandButton, IconButton } from 'office-ui-fabric-react/lib/Button';
import { Spinner, SpinnerType } from 'office-ui-fabric-react/lib/Spinner';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';
import { logUsage } from 'owa-analytics';
import { ControlIcons } from 'owa-control-icons';
import {
    ImageGalleryItem,
    ImagePreviewState,
    lazySetImagePreviewLoaded,
    lazySetImagePreviewStateFromImageGalleryItem,
} from 'owa-sxsdata';
import * as React from 'react';
import { SxSCustomData } from '../utils/combineCustomData';
import { exitFullScreen } from '../utils/fullScreenUtilities';
import isImageGalleryItemCurrentImagePreview from '../utils/isImageGalleryItemCurrentImagePreview';
import ImagePreviewFilmStrip from './ImagePreviewFilmStrip';

export interface ImagePreviewArrowCallbacks {
    left: () => void;
    right: () => void;
}

export interface ImagePreviewProps {
    arrowCallbacks: ImagePreviewArrowCallbacks;
    className?: string;
    getExtraCustomDataForDatapoint: () => SxSCustomData;
    onBeforeImageGalleryItemSelected: (imageGalleryItem: ImageGalleryItem) => Promise<void>;
    onError: (message: string, error?: Error) => void;
    onImageGalleryItemSelected: () => void;
    onLoad: () => void;
    viewState: ImagePreviewState;
}

const styles = require('./ImagePreview.scss');
const classNames = require('classnames/bind').bind(styles);

export interface ImagePreviewHandle {
    focus(): void;
    onResize(): void;
    getFullScreenDiv(): HTMLDivElement;
}

export default observer(
    function ImagePreview(props: ImagePreviewProps, ref: React.Ref<ImagePreviewHandle>) {
        const currentIndex = React.useRef(-1);
        const imageDiv = React.useRef<HTMLElement>();
        const fullScreenDiv = React.useRef<HTMLDivElement>();
        const filmStrip = React.useRef<ImagePreviewFilmStrip>();

        React.useImperativeHandle(
            ref,
            () => ({
                focus() {
                    requestAnimationFrame(() => {
                        if (imageDiv.current) {
                            imageDiv.current.focus();
                        }
                    });
                },
                onResize() {
                    if (filmStrip.current) {
                        filmStrip.current.onResize();
                    }
                },
                getFullScreenDiv() {
                    return fullScreenDiv.current;
                },
            }),
            []
        );
        const onImageLoad = async () => {
            const setImagePreviewLoaded = await lazySetImagePreviewLoaded.import();
            setImagePreviewLoaded(props.viewState);
            props.onLoad();
        };
        const onError = () => {
            props.onError(loc(errorImageAttachment), new Error('Image fails to load'));
        };
        const onKeyDown = (evt: React.KeyboardEvent<unknown>) => {
            const triggeredBy = 'keyboardNav';
            const imageGallery = props.viewState.imageGallery;
            switch (evt.which) {
                case KeyCodes.left:
                    if (props.arrowCallbacks) {
                        if (props.arrowCallbacks.left) {
                            props.arrowCallbacks.left();
                        }
                    } else if (imageGallery) {
                        if (currentIndex.current > 0) {
                            const previousImage = imageGallery[currentIndex.current - 1];
                            setPreviewToGalleryItem(previousImage, triggeredBy);
                        } else if (currentIndex.current === 0) {
                            // wrap to last image if current is first image
                            const lastImage = imageGallery[imageGallery.length - 1];
                            setPreviewToGalleryItem(lastImage, triggeredBy);
                        }
                    }
                    evt.preventDefault();
                    evt.stopPropagation();
                    break;
                case KeyCodes.right:
                    if (props.arrowCallbacks) {
                        if (props.arrowCallbacks.right) {
                            props.arrowCallbacks.right();
                        }
                    } else if (imageGallery && currentIndex.current >= 0) {
                        if (currentIndex.current < imageGallery.length - 1) {
                            const nextImage = imageGallery[currentIndex.current + 1];
                            setPreviewToGalleryItem(nextImage, triggeredBy);
                        } else {
                            // wrap to first image if current is last image
                            const firstImage = imageGallery[0];
                            setPreviewToGalleryItem(firstImage, triggeredBy);
                        }
                    }
                    evt.preventDefault();
                    evt.stopPropagation();
                    break;
            }
        };
        const setPreviewToGalleryItem = async (
            imageGalleryItem: ImageGalleryItem,
            triggeredBy: string
        ) => {
            if (props.onBeforeImageGalleryItemSelected) {
                await props.onBeforeImageGalleryItemSelected(imageGalleryItem);
            }
            const setImagePreviewStateFromImageGalleryItem = await lazySetImagePreviewStateFromImageGalleryItem.import();
            setImagePreviewStateFromImageGalleryItem(props.viewState, imageGalleryItem);
            const extraCustomData = props.getExtraCustomDataForDatapoint
                ? props.getExtraCustomDataForDatapoint()
                : null;
            logUsage(
                'SxSImagePreviewNavigation',
                {
                    triggeredBy: triggeredBy,
                    ...extraCustomData?.customData,
                },
                {
                    cosmosOnlyData:
                        extraCustomData && JSON.stringify(extraCustomData.cosmosOnlyData),
                }
            );
            if (props.onImageGalleryItemSelected) {
                props.onImageGalleryItemSelected();
            }
        };
        const mainClassNames = classNames(props.className, styles.main);
        const previousArrowContainerClassNames = classNames(
            styles.arrowContainer,
            styles.prevArrowContainer
        );
        const nextArrowContainerClassNames = classNames(
            styles.arrowContainer,
            styles.nextArrowContainer
        );
        let onNextButtonClicked = null;
        let onPreviousButtonClicked = null;
        const imageGallery = props.viewState.imageGallery;
        if (props.arrowCallbacks) {
            onNextButtonClicked = props.arrowCallbacks.right;
            onPreviousButtonClicked = props.arrowCallbacks.left;
        } else if (imageGallery) {
            currentIndex.current = -1;
            for (let i = 0; i < imageGallery.length; i++) {
                if (isImageGalleryItemCurrentImagePreview(imageGallery[i], props.viewState)) {
                    currentIndex.current = i;
                    break;
                }
            }
            if (currentIndex.current !== -1) {
                if (currentIndex.current > 0) {
                    const previousImage = imageGallery[currentIndex.current - 1];
                    onPreviousButtonClicked = () =>
                        setPreviewToGalleryItem(previousImage, 'arrowButton');
                }
                if (currentIndex.current < imageGallery.length - 1) {
                    const nextImage = imageGallery[currentIndex.current + 1];
                    onNextButtonClicked = () => setPreviewToGalleryItem(nextImage, 'arrowButton');
                }
            }
        }
        // Note: the span element before the img element is needed
        // to vertically center the image correctly
        return (
            <div ref={fullScreenDiv} className={mainClassNames}>
                <div
                    className={styles.imageContainer}
                    ref={ref => (imageDiv.current = ref)}
                    onKeyDown={onKeyDown}
                    data-is-focusable={true}
                    tabIndex={0}>
                    <span />
                    <img
                        className={
                            !props.viewState.imageLoaded || props.viewState.isImageLoading
                                ? styles.imageLoading
                                : null
                        }
                        onError={onError}
                        onLoad={onImageLoad}
                        src={props.viewState.previewUrl}
                        alt={loc(imagePreviewAltText)}
                    />
                    {props.viewState.isImageLoading && <Spinner type={SpinnerType.large} />}
                </div>
                {onPreviousButtonClicked && (
                    <IconButton
                        onClick={onPreviousButtonClicked}
                        iconProps={{
                            iconName: ControlIcons.ChevronLeft,
                            styles: {
                                root: styles.arrowButton,
                            },
                        }}
                        styles={{
                            root: previousArrowContainerClassNames,
                        }}
                        title={loc(previousAttachment)}
                        ariaLabel={loc(previousAttachment)}
                    />
                )}
                {onNextButtonClicked && (
                    <IconButton
                        onClick={onNextButtonClicked}
                        iconProps={{
                            iconName: ControlIcons.ChevronRight,
                            styles: {
                                root: styles.arrowButton,
                            },
                        }}
                        styles={{
                            root: nextArrowContainerClassNames,
                        }}
                        title={loc(nextAttachment)}
                        ariaLabel={loc(nextAttachment)}
                    />
                )}
                <CommandButton
                    onClick={exitFullScreen}
                    className={styles.exitFullScreenButton}
                    iconProps={{
                        iconName: ControlIcons.Cancel,
                        styles: { root: styles.exitFullScreenIcon },
                    }}
                    text={loc(exitFullScreen_1)}
                />
                {!props.arrowCallbacks && (
                    <ImagePreviewFilmStrip
                        ref={r => (filmStrip.current = r)}
                        setPreviewToGalleryItem={setPreviewToGalleryItem}
                        viewState={props.viewState}
                    />
                )}
            </div>
        );
    },
    { forwardRef: true }
);
